import React from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import type { File } from '../../../../types/file.types';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { SortIcons } from '../FileListTable/SortIcons';
import {
  MdOutlineMarkEmailUnread,
  MdMarkEmailUnread,
  MdDeleteOutline,
} from 'react-icons/md';
import TrashRestore from '@/components/icons/TrashRestore';
import TrashRestoreOutline from '@/components/icons/TrashRestoreOutline';
import { useFileActions } from '../../../../../../hooks/useFileActions';

interface DateCellProps {
  date: string;
  id: string;
  isRead: boolean;
  isArchived: boolean;
}

const DateCell: React.FC<DateCellProps> = ({
  date,
  id,
  isRead,
  isArchived,
}) => {
  const { toggleRead, toggleArchive, isLoading, deleteFile } = useFileActions();

  const handleToggleRead = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isLoading) {
      await toggleRead(id, isRead);
    }
  };

  const handleToggleArchive = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isLoading) {
      await toggleArchive(id, isArchived);
    }
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm('Voulez-vous vraiment supprimer ce fichier ?')) {
      if (!isLoading) {
        await deleteFile(id);
      }
    }
  };

  return (
    <div className="relative flex w-full items-center justify-end pr-4">
      <div className="text-sm text-gray-500 transition-opacity duration-200 group-hover:opacity-0">
        {format(new Date(date), 'dd/MM/yyyy', { locale: fr })}
      </div>
      <div className="absolute inset-0 flex items-center justify-end gap-2 pr-4 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
        <button
          onClick={handleToggleRead}
          disabled={isLoading}
          className={`flex h-8 w-8 items-center justify-center rounded-md bg-white text-[#2f2f2f] shadow-sm transition-all duration-300 hover:bg-[#eaeaed] dark:bg-[#333135] dark:text-white dark:hover:bg-[#545454] ${
            isLoading ? 'cursor-not-allowed opacity-50' : ''
          }`}
          data-tooltip-content={
            isRead ? 'Marquer comme non lu' : 'Marquer comme lu'
          }
          data-tooltip-id="tooltip"
          data-tooltip-place="bottom"
          data-tooltip-delay-show={100}
          data-tooltip-offset={5}
        >
          {isRead ? (
            <MdMarkEmailUnread className="text-lg" />
          ) : (
            <MdOutlineMarkEmailUnread className="text-lg" />
          )}
        </button>

        <button
          onClick={handleToggleArchive}
          disabled={isLoading}
          className={`flex h-8 w-8 items-center justify-center rounded-md bg-white text-[#2f2f2f] shadow-sm transition-all duration-300 hover:bg-[#eaeaed] dark:bg-[#333135] dark:text-white dark:hover:bg-[#545454] ${
            isLoading ? 'cursor-not-allowed opacity-50' : ''
          }`}
          data-tooltip-content={isArchived ? 'Désarchiver' : 'Archiver'}
          data-tooltip-id="tooltip"
          data-tooltip-place="bottom"
          data-tooltip-delay-show={100}
          data-tooltip-offset={5}
        >
          {isArchived ? (
            <TrashRestore className="text-lg" />
          ) : (
            <TrashRestoreOutline className="text-lg" />
          )}
        </button>
        {isArchived && (
          <button
            data-tooltip-content="Supprimer"
            data-tooltip-id="tooltip"
            data-tooltip-place="bottom"
            data-tooltip-delay-show={100}
            data-tooltip-offset={5}
            onClick={handleDelete}
            disabled={isLoading}
            className="flex h-8 w-8 items-center justify-center rounded-md bg-white text-[#2f2f2f] shadow-sm transition-all duration-300 hover:bg-[#eaeaed] dark:bg-[#333135] dark:text-white dark:hover:bg-[#545454]"
          >
            <MdDeleteOutline className="text-xl text-[red]" />
          </button>
        )}
      </div>
    </div>
  );
};

export const DateColumn: ColumnDef<File> = {
  id: 'received_timestamp',
  accessorFn: (row) => row.received_timestamp,
  header: ({ column }) => (
    <button
      onClick={() => column.toggleSorting()}
      className="flex w-full items-center justify-end gap-1 pr-4"
    >
      Date
      <SortIcons sortDirection={column.getIsSorted()} />
    </button>
  ),
  cell: (props) => (
    <DateCell
      date={props.getValue() as string}
      id={props.row.original.id}
      isRead={props.row.original.lu}
      isArchived={props.row.original.archive}
    />
  ),
  enableSorting: true,
  size: 120,
};
